import React, { useState, useEffect } from 'react';
import API_URL from './config';
import './PublicCalendar.css';

function PublicCalendar() {
  const [bookings, setBookings] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'week'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null); // For modal
  const [timezone, setTimezone] = useState('America/New_York');

  // Get display name for timezone
  const getTimezoneDisplay = (tz) => {
    const tzMap = {
      'America/New_York': 'Eastern Time (ET)',
      'America/Chicago': 'Central Time (CT)',
      'America/Denver': 'Mountain Time (MT)',
      'America/Los_Angeles': 'Pacific Time (PT)',
      'America/Anchorage': 'Alaska Time (AKT)',
      'Pacific/Honolulu': 'Hawaii Time (HT)',
      'America/Phoenix': 'Arizona Time (MST)',
      'America/Indiana/Indianapolis': 'Indiana (ET)',
    };
    return tzMap[tz] || tz;
  };

  // Fetch timezone setting
  useEffect(() => {
    fetch(`${API_URL}/api/settings/timezone`)
      .then(r => r.json())
      .then(data => { if (data.value) setTimezone(data.value); })
      .catch(() => {});
  }, []);

  // Fetch public availability data
  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/api/public/availability`);
        if (!res.ok) throw new Error('Failed to fetch availability');
        const data = await res.json();
        setBookings(data);
        setError(null);
      } catch (err) {
        setError('Unable to load calendar data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailability();
    // Refresh every 15 minutes
    const interval = setInterval(fetchAvailability, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Navigation functions
  const goToToday = () => setCurrentDate(new Date());
  
  const goToPrev = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setDate(newDate.getDate() - 7);
    }
    setCurrentDate(newDate);
  };
  
  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else {
      newDate.setDate(newDate.getDate() + 7);
    }
    setCurrentDate(newDate);
  };

  // Get calendar days for month view
  const getMonthDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const current = new Date(startDate);
    
    // Generate 6 weeks (42 days) to ensure full calendar grid
    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  // Get bookings for a specific date
  const getBookingsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return bookings.filter(b => b.date === dateStr);
  };

  // Format time for display (e.g., "9am", "2:30pm")
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    // Include minutes if not :00
    if (minutes && minutes !== 0) {
      return `${displayHours}:${minutes.toString().padStart(2, '0')}${period}`;
    }
    return `${displayHours}${period}`;
  };

  // Format time range for compact display
  const formatTimeRange = (start, end) => {
    return `${formatTime(start)}-${formatTime(end)}`;
  };

  // Get resource color
  const getResourceColor = (resource) => {
    const colors = {
      'Full Turf': '#1a73e8',
      'Half Turf': '#34a853',
      'Batting Cage': '#ea4335'
    };
    return colors[resource] || '#5f6368';
  };

  // Format month/year header
  const formatMonthYear = () => {
    return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Format full date for modal header
  const formatFullDate = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Handle clicking on "+X more"
  const handleMoreClick = (day, dayBookings) => {
    setSelectedDay({ date: day, bookings: dayBookings });
  };

  // Close modal
  const closeModal = () => {
    setSelectedDay(null);
  };

  const weekDays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const monthDays = getMonthDays();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (loading) {
    return (
      <div className="public-calendar">
        <div className="calendar-loading">Loading calendar...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-calendar">
        <div className="calendar-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="public-calendar">
      {/* Calendar Header */}
      <div className="calendar-header">
        <div className="calendar-nav">
          <button className="calendar-today-btn" onClick={goToToday}>Today</button>
          <button className="calendar-nav-btn" onClick={goToPrev} aria-label="Previous">
            &#8249;
          </button>
          <button className="calendar-nav-btn" onClick={goToNext} aria-label="Next">
            &#8250;
          </button>
          <h2 className="calendar-title">{formatMonthYear()}</h2>
        </div>
        <div className="calendar-actions">
          <select 
            className="calendar-view-select" 
            value={viewMode} 
            onChange={(e) => setViewMode(e.target.value)}
          >
            <option value="month">Month</option>
            <option value="week">Week</option>
          </select>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="calendar-grid" style={{ display: 'block', width: '100%' }}>
        {/* Week day headers */}
        <div className="calendar-weekdays" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', width: '100%' }}>
          {weekDays.map(day => (
            <div key={day} className="calendar-weekday">{day}</div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="calendar-days" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', width: '100%' }}>
          {monthDays.map((day, idx) => {
            const isCurrentMonth = day.getMonth() === currentDate.getMonth();
            const isToday = day.toDateString() === today.toDateString();
            const dayBookings = getBookingsForDate(day);
            
            return (
              <div 
                key={idx} 
                className={`calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''}`}
              >
                <div className={`day-number ${isToday ? 'today-number' : ''}`}>
                  {day.getDate()}
                </div>
                <div className="day-bookings">
                  {dayBookings.slice(0, 3).map((booking, bIdx) => (
                    <div 
                      key={bIdx} 
                      className="booking-pill"
                      style={{ backgroundColor: getResourceColor(booking.resource) }}
                      title={`${formatTime(booking.start)} - ${formatTime(booking.end)} ${booking.resource}`}
                    >
                      <span className="booking-time">{formatTimeRange(booking.start, booking.end)}</span>
                      <span className="booking-resource">{booking.resource}</span>
                    </div>
                  ))}
                  {dayBookings.length > 3 && (
                    <div 
                      className="more-bookings"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoreClick(day, dayBookings);
                      }}
                    >
                      +{dayBookings.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#1a73e8' }}></span>
          <span>Full Turf</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#34a853' }}></span>
          <span>Half Turf</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#ea4335' }}></span>
          <span>Batting Cage</span>
        </div>
      </div>

      {/* Footer info */}
      <div className="calendar-footer-info">
        <span>Granger Sports Complex</span>
        <span className="calendar-timezone">{getTimezoneDisplay(timezone)}</span>
      </div>

      {/* Day Bookings Modal */}
      {selectedDay && (
        <div className="day-modal-overlay" onClick={closeModal}>
          <div className="day-modal" onClick={(e) => e.stopPropagation()}>
            <div className="day-modal-header">
              <h3>{formatFullDate(selectedDay.date)}</h3>
              <button className="day-modal-close" onClick={closeModal}>&times;</button>
            </div>
            <div className="day-modal-content">
              {selectedDay.bookings
                .sort((a, b) => a.start.localeCompare(b.start))
                .map((booking, idx) => (
                  <div 
                    key={idx} 
                    className="modal-booking-item"
                    style={{ borderLeftColor: getResourceColor(booking.resource) }}
                  >
                    <span className="modal-booking-time">
                      {formatTime(booking.start)} - {formatTime(booking.end)}
                    </span>
                    <span 
                      className="modal-booking-resource"
                      style={{ backgroundColor: getResourceColor(booking.resource) }}
                    >
                      {booking.resource}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PublicCalendar;
